define((require) => {
    "use strict";

    const _ = require("underscore");

    /**
     * The Injector is used to register specific instances identified by a given
     * token, e.g. <tt>Injector.register("mainController", new MainController())</tt>;
     *
     * The Injector is part of the <tt>zbone/Extend</tt> module, that enables DI at the
     * very core.
     *
     * A typical class definition, using DI, would look like this:
     *
     * <tt>
     * Class.extend({
     *     constructor: function (foo, boo) {
     *
     *     }
     * }, {
     *     injection: ["foo", "boo"]
     * });
     * </tt>
     *
     * The <tt>injection</tt> hash also supports wrapping the arguments in a single "options"
     * hash (the Backbone way):
     *
     * <tt>
     * injection: {
     *     parameters: ["foo", "boo"],
     *     asOptions: true
     * }
     * </tt>
     *
     * @class Injector
     * @constructor
     */
    const Injector = function() {
        this._registry = {};
    };

    Injector.prototype = {

        _createParametersArray(parameters, additionalArguments) {
            let instances = [];

            _.each(parameters, (value) => {
                const instance = this.get(value);
                if (!instance) {
                    throw new Error(`${value} has not been registered.`);
                }
                instances.push(instance);
            }, this);

            if (additionalArguments) {
                instances = instances.concat(additionalArguments);
            }

            return instances;
        },

        _createParametersObject(parameters, additionalArguments) {
            const options = {};

            _.each(parameters, (value) => {
                const instance = this.get(value);
                if (!instance) {
                    throw new Error(`${value} has not been registered.`);
                }
                options[value] = instance;
            });

            // Apply any additional options to the parsed hash of options
            if (additionalArguments && additionalArguments.length === 1) {
                const additionalOptions = additionalArguments[0];
                _.extend(options, additionalOptions);
            }

            return [options];
        },

        getParameters(injectionOptions, additionalArguments) {
            const parameters = _.isArray(injectionOptions) ? injectionOptions :
                injectionOptions.parameters;

            return injectionOptions.asOptions ? this._createParametersObject(parameters, additionalArguments) :
                this._createParametersArray(parameters, additionalArguments);
        },

        register(identifier, instance) {
            this._registry[identifier] = instance;
        },

        has(identifier) {
            return this._registry[identifier] !== undefined;
        },

        get(identifier) {
            if (!this.has(identifier)) {
                throw new Error(`'${identifier}' has not been registered with the Injector.`);
            }
            return this._registry[identifier];
        }
    };

    // Singleton
    return new Injector();
});



// WEBPACK FOOTER //
// ./src/shared/util/Injector.js